import * as React from "react";
import type { RouteComponentProps } from "react-router";
import { withRouter } from "react-router";
import { ProcessType } from "~/client/resources";
import ActionList from "~/components/ActionList";
import type { DoBusyTask } from "~/components/DataBaseComponent";
import { OverflowMenu } from "~/components/OverflowMenu/OverflowMenu";
import type { MenuItem } from "~/components/OverflowMenu/OverflowMenu";
import type { PaperLayoutProps } from "~/components/PaperLayout";
import routeLinks from "~/routeLinks";
import StringHelper from "~/utils/StringHelper";
import { FormTitleForRunbook } from "../../Runbooks/LastPublishedChip";
import { RunbooksPaperLayout } from "../../Runbooks/Layouts";
import PublishButton from "../../Runbooks/PublishButton";
import ContextualRunNowButton from "../../Runbooks/RunNowButton";
import { useRunbookContext, useOptionalRunbookContext } from "../../Runbooks/RunbookContext";
import ContextAddStepNavigationButton from "../../Steps/ContextAddStepNavigationButton";
import { useProcessContext } from "../Contexts/ProcessContext";
import { ProcessPaperLayout } from "./ProcessPaperLayout";

interface ProcessContextPaperLayoutPropsInternal {
    processType: ProcessType;
    overflowMenuItems: Array<MenuItem | MenuItem[]>;
    doBusyTask: DoBusyTask;
}

type ProcessContextPaperLayoutProps = ProcessContextPaperLayoutPropsInternal & PaperLayoutProps & RouteComponentProps<{ projectSlug: string }>;
const ProcessContextPaperLayout: React.FC<ProcessContextPaperLayoutProps> = ({ children, ...rest }) => {
    const processContext = useProcessContext();
    const hasSteps = processContext.selectors.hasSteps();
    const projectSlug = rest.match.params.projectSlug;

    const runbookContext = useOptionalRunbookContext();
    const runbookName = runbookContext?.state.runbook?.Name ?? StringHelper.ellipsis;

    if (rest.processType === ProcessType.Runbook) {
        return (
            <RunbooksProcessContextPaperLayout title={runbookName} breadcrumbTitle="Runbooks" hasSteps={hasSteps} projectSlug={projectSlug} {...rest}>
                {children}
            </RunbooksProcessContextPaperLayout>
        );
    } else {
        return (
            <DeploymentProcessContextPaperLayout title={"Process"} projectSlug={projectSlug} {...rest}>
                {children}
            </DeploymentProcessContextPaperLayout>
        );
    }
};

export const EnhancedProcessContextPaperLayout = withRouter(ProcessContextPaperLayout);

const DeploymentProcessContextPaperLayout: React.FC<ProcessContextPaperLayoutProps & { projectSlug: string }> = ({ projectSlug, ...props }) => {
    const actions = [<ContextAddStepNavigationButton />];
    if (props.overflowMenuItems.length > 0) {
        actions.push(<OverflowMenu menuItems={props.overflowMenuItems} />);
    }
    return (
        <ProcessPaperLayout sectionControl={<ActionList actions={actions} />} {...props}>
            {props.children}
        </ProcessPaperLayout>
    );
};

const RunbooksProcessContextPaperLayout: React.FC<ProcessContextPaperLayoutProps & { projectSlug: string; hasSteps: boolean; doBusyTask: DoBusyTask }> = ({ projectSlug, hasSteps, doBusyTask, ...props }) => {
    const runbooksContext = useRunbookContext();
    const title = <FormTitleForRunbook runbookName={runbooksContext.state.runbook?.Name ?? ""} />;
    const actions = [<PublishButton doBusyTask={doBusyTask} />, <ContextualRunNowButton isDisabled={!hasSteps} />, <ContextAddStepNavigationButton />];
    if (props.overflowMenuItems.length > 0) {
        actions.push(<OverflowMenu menuItems={props.overflowMenuItems} />);
    }
    return (
        <RunbooksPaperLayout title={title} breadcrumbTitle={"Runbooks"} breadcrumbPath={routeLinks.project(projectSlug).operations.runbooks} sectionControl={<ActionList actions={actions} />} {...props}>
            {props.children}
        </RunbooksPaperLayout>
    );
};
